import { MuiAlert, strIsNullOrEmpty } from '@fox/atoms';
import { ResponsiveTable, ResponsiveTableColumn } from '@fox/data-grid';
import { useContentContext } from '../../content-context';
import { useActionCentreContext } from './action-centre-context';
import { useActionCentre } from './action-centre-data';
import { ActionCentreColumn, CardViewSettings, SwiftFoxActionCentreResult, filterOutImageColumns } from './contracts';

export function ActionCentreTable({
	actionId,
	rowsExpandedOnSmallScreens,
	actionCentreColumns,
	pageSize,
	cardViewSettings
}: {
	actionId: string;
	rowsExpandedOnSmallScreens: boolean;
	actionCentreColumns: ActionCentreColumn[];
	pageSize: number;
	cardViewSettings?: CardViewSettings;
}) {
	const { contentId, requestToken } = useContentContext();
	const { filters, page, sort, sourceFilter, setSort, setPage } = useActionCentreContext();

	const { data, error, loading } = useActionCentre(
		{
			contentId,
			id: actionId,
			pageSize: pageSize,
			filters,
			page: page - 1, // Adjust for 0-based index
			sourceFilter,
			sort
		},
		requestToken
	);

	if (!strIsNullOrEmpty(error)) return <MuiAlert severity="error">{error}</MuiAlert>;

	// Filter out image columns and thumbnail field from table view
	const filteredColumns = filterOutImageColumns(actionCentreColumns, cardViewSettings);

	const columns: ResponsiveTableColumn<unknown>[] = filteredColumns?.map((column, i) => {
		return {
			title: column.title,
			dataIndex: column.id,
			showOnMobileRow: i <= 1,
			sortable: !!column.sortIndex
		};
	});

	return (
		<ResponsiveTable<SwiftFoxActionCentreResult['data'][0]>
			loading={loading}
			data={data?.data ?? []}
			rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens}
			sortProps={{
				sort: sort?.column ? { column: sort?.column, asc: sort?.asc } : undefined,
				onSort: sortState => {
					const sortIndex = actionCentreColumns.find(c => c.id === sortState.column)?.sortIndex;
					if (sortIndex === undefined) return;
					setSort({ column: sortIndex, asc: sortState.asc });
				}
			}}
			columns={columns}
			paginationProps={
				data?.totalResults
					? {
							page,
							onPageChange: page => setPage(page),
							total: data?.totalResults,
							itemsPerPage: pageSize
					  }
					: undefined
			}
		/>
	);
}
